import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';
import { formatDuration, formatDurationShort } from '../lib/format.js';

// Same pattern as TodayTasks/TaskSearch — see comment there. Light-mode
// variants on the hardcoded Tailwind colors (blue / cyan) so the pills
// stay readable on white.
const STATUS_COLOR = {
  'To Do': 'text-blue-700 bg-blue-50 border-blue-700/40 dark:text-blue-300 dark:bg-blue-500/10 dark:border-blue-500/30',
  'In Progress': 'text-cyan-700 bg-cyan-50 border-cyan-700/40 dark:text-cyan-300 dark:bg-cyan-500/10 dark:border-cyan-500/30',
  'Blocked': 'text-danger bg-danger/10 border-danger/30',
  'Complete': 'text-running bg-running/10 border-running/30',
  'On Hold': 'text-warn bg-warn/10 border-warn/30'
};

export default function TaskView({ task, runningItem, queue, queueBusy, now, employee, onStart, onPause, onResume, onComplete, onBack, onDelete }) {
  const [subtasks, setSubtasks] = useState(null);
  const [error, setError] = useState(null);
  // Task status dropdown — per-base status options (fetched once,
  // cached for 5min). Optimistically reflected via localStatus so the
  // UI updates immediately while the PATCH is in flight.
  const [statusOptions, setStatusOptions] = useState([]);
  const [localStatus, setLocalStatus] = useState(task.status || '');
  const [statusBusy, setStatusBusy] = useState(false);
  const [statusError, setStatusError] = useState(null);

  // Keep local status in sync when the task prop changes (parent refresh).
  useEffect(() => { setLocalStatus(task.status || ''); }, [task.status]);

  useEffect(() => {
    let cancelled = false;
    api.taskStatusOptions()
      .then(d => { if (!cancelled) setStatusOptions(d.choices || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const handleStatusChange = async (next) => {
    if (!next || next === localStatus) return;
    const previous = localStatus;
    setLocalStatus(next);
    setStatusBusy(true);
    setStatusError(null);
    try {
      await api.setTaskStatus(employee.id, task.id, next);
    } catch (e) {
      setLocalStatus(previous);
      setStatusError(e.message || 'Failed to update status');
    } finally {
      setStatusBusy(false);
    }
  };

  const loadSubtasks = () => {
    setError(null);
    api.subtasks(task.id, task.subTaskIds)
      .then(d => setSubtasks(d.subtasks))
      .catch(e => setError(e.message));
  };

  useEffect(() => {
    setSubtasks(null);
    loadSubtasks();
  }, [task.id]);

  // When any of this task's sub-tasks changes state in the queue (Start /
  // Pause / Complete / Next), pull fresh sub-task records so the embedded
  // status pills and totals match the queue panel. Without this, TaskView
  // shows stale "To Do" pills until the user navigates away and back.
  const myQueueSig = (queue || [])
    .filter(q => task.subTaskIds?.includes(q.subTaskId))
    .map(q => `${q.subTaskId}:${q.state}:${q.logId}`)
    .sort()
    .join('|');
  useEffect(() => {
    if (subtasks === null) return; // initial load handled above
    loadSubtasks();
  }, [myQueueSig]);

  // `now` is threaded down from TimeTracker — single 1Hz tick for the
  // whole tree. The previous local setInterval here ran unconditionally
  // even with no running timer, and drifted from the dashboard chip.
  const tickNow = typeof now === 'number' ? now : Date.now();

  const queueBySubTask = new Map((queue || []).map(q => [q.subTaskId, q]));

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={onBack}
          className="text-muted hover:text-text text-sm flex items-center gap-1"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M15 18l-6-6 6-6" />
          </svg>
          Back to search
        </button>
        {onDelete && (
          <button
            onClick={() => onDelete(task)}
            title="Delete this task"
            className="text-xs text-muted hover:text-danger flex items-center gap-1 px-2 py-1 rounded-md hover:bg-danger/10 border border-transparent hover:border-danger/30 transition-colors"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6" />
              <path d="M10 11v6M14 11v6" />
            </svg>
            Delete
          </button>
        )}
      </div>

      <div className="rounded-lg border border-border bg-panel p-5 mb-4">
        <div className="text-xs uppercase tracking-wider text-muted mb-1">Task</div>
        <h2 className="text-xl font-semibold">{task.name}</h2>
        <div className="mt-2 flex items-center gap-2 flex-wrap">
          {/* Read-only colored pill mirrors the dropdown selection so
              the existing visual language survives. */}
          {localStatus && (
            <span className={`inline-block text-[11px] px-2 py-0.5 rounded font-medium border ${STATUS_COLOR[localStatus] || 'text-muted bg-panel2 border-border'}`}>
              {localStatus}
            </span>
          )}
          {statusOptions.length > 0 && (
            <select
              value={localStatus}
              disabled={statusBusy}
              onChange={e => handleStatusChange(e.target.value)}
              title="Change task status"
              className="text-[11px] px-2 py-1 rounded-md bg-panel2 border border-border text-text focus:border-accent focus:outline-none disabled:opacity-50"
            >
              {/* If the current value isn't in the list (legacy data),
                  surface it as a disabled option so it shows but can't
                  be re-selected accidentally. */}
              {!statusOptions.some(o => o.name === localStatus) && localStatus && (
                <option value={localStatus} disabled>{localStatus} (current)</option>
              )}
              {statusOptions.map(o => (
                <option key={o.id || o.name} value={o.name}>{o.name}</option>
              ))}
            </select>
          )}
          {statusBusy && (
            <span className="text-[11px] text-muted">Saving…</span>
          )}
          {statusError && (
            <span className="text-[11px] text-danger">{statusError}</span>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-3 p-3 rounded-lg border border-danger/30 bg-danger/10 text-sm text-danger flex items-center justify-between">
          <span>{error}</span>
          <button onClick={loadSubtasks} className="underline">Retry</button>
        </div>
      )}

      {subtasks === null && !error && (
        <div className="text-center py-12 text-muted text-sm">Loading sub-tasks...</div>
      )}

      {subtasks && subtasks.length === 0 && (
        <div className="text-center py-12 text-muted text-sm">No sub-tasks linked to this task.</div>
      )}

      {subtasks && subtasks.length > 0 && (
        <>
          <div className="flex items-center justify-between mb-2 px-1">
            <div className="text-[10px] uppercase tracking-wider text-muted font-semibold">
              Sub-tasks · {subtasks.length}
            </div>
          </div>
          <div className="space-y-2">
          {subtasks.map(sub => {
            const queueEntry = queueBySubTask.get(sub.id);
            const isRunning = queueEntry?.state === 'running';
            const isPaused = queueEntry?.state === 'paused';
            const inQueue = Boolean(queueEntry);
            const busy = queueBusy?.[sub.id];

            const liveElapsed = isRunning && queueEntry?.startTime
              ? Math.max(0, Math.floor((tickNow - new Date(queueEntry.startTime).getTime()) / 1000))
              : 0;

            return (
              <div
                key={sub.id}
                className={`rounded-lg border p-4 transition-colors ${
                  isRunning ? 'border-running bg-running/10'
                    : isPaused ? 'border-warn/40 bg-warn/5'
                    : 'border-border bg-panel hover:bg-panel2'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-[9px] uppercase tracking-wider text-muted/70 font-semibold mb-0.5">Sub-task</div>
                    <div className="flex items-start gap-2">
                      {isRunning && <div className="mt-1.5 w-2.5 h-2.5 rounded-full bg-running pulse-dot flex-shrink-0" />}
                      {isPaused && <div className="mt-1.5 w-2.5 h-2.5 rounded-full bg-warn flex-shrink-0" />}
                      <div className="font-medium">{sub.name}</div>
                    </div>
                    <div className="flex items-center flex-wrap gap-2 mt-2">
                      <span className={`text-[11px] px-2 py-0.5 rounded font-medium border ${STATUS_COLOR[sub.status] || 'text-muted bg-panel2 border-border'}`}>
                        {sub.status}
                      </span>
                      {sub.totalSeconds > 0 && (
                        <span className="text-xs text-muted font-mono">
                          Total: {formatDurationShort(sub.totalSeconds)}
                        </span>
                      )}
                      {isRunning && (
                        <span className="text-xs font-mono font-semibold text-running">
                          {formatDuration(liveElapsed)}
                        </span>
                      )}
                      {isPaused && queueEntry.elapsedSeconds > 0 && (
                        <span className="text-xs font-mono text-warn">
                          paused at {formatDuration(queueEntry.elapsedSeconds)}
                        </span>
                      )}
                    </div>
                    {sub.objective && (
                      <div className="text-xs text-muted mt-2 line-clamp-2">{sub.objective}</div>
                    )}
                  </div>

                  <div className="flex-shrink-0 flex flex-col gap-1.5">
                    {!inQueue && (
                      <button
                        onClick={() => onStart(sub.id)}
                        disabled={Boolean(busy)}
                        className="px-4 py-2 rounded-md bg-accent hover:bg-accent2 text-white text-sm font-semibold transition-colors"
                      >
                        {busy === 'start' ? '...' : 'Start'}
                      </button>
                    )}
                    {isRunning && (
                      <>
                        <button
                          onClick={() => onPause(queueEntry)}
                          disabled={Boolean(busy)}
                          className="px-3 py-1.5 rounded-md border border-warn/40 text-warn hover:bg-warn/10 text-xs font-semibold transition-colors"
                        >
                          {busy === 'pause' ? '...' : '⏸ On Hold'}
                        </button>
                        <button
                          onClick={() => onComplete(queueEntry)}
                          disabled={Boolean(busy)}
                          className="px-3 py-1.5 rounded-md border border-running/40 text-running hover:bg-running/10 text-xs font-semibold transition-colors"
                        >
                          {busy === 'complete' ? '...' : '✓ Complete'}
                        </button>
                      </>
                    )}
                    {isPaused && !queueEntry?.atMaxDuration && (
                      <>
                        <button
                          onClick={() => onResume(queueEntry)}
                          disabled={Boolean(busy)}
                          className="px-3 py-1.5 rounded-md border border-cyan-700/50 text-cyan-700 hover:bg-cyan-100 dark:border-cyan-500/40 dark:text-cyan-300 dark:hover:bg-cyan-500/10 text-xs font-semibold transition-colors"
                        >
                          {busy === 'resume' ? '...' : '▶ Resume'}
                        </button>
                        <button
                          onClick={() => onComplete(queueEntry)}
                          disabled={Boolean(busy)}
                          className="px-3 py-1.5 rounded-md border border-running/40 text-running hover:bg-running/10 text-xs font-semibold transition-colors"
                        >
                          {busy === 'complete' ? '...' : '✓ Complete'}
                        </button>
                      </>
                    )}
                    {isPaused && queueEntry?.atMaxDuration && (
                      <button
                        onClick={() => onComplete(queueEntry)}
                        disabled={Boolean(busy)}
                        className="px-3 py-1.5 rounded-md border border-running/40 text-running hover:bg-running/10 text-xs font-semibold transition-colors"
                        title="Resume disabled — 4h cap reached. Mark complete or start fresh from the parent task."
                      >
                        {busy === 'complete' ? '...' : '✓ Complete'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          </div>
        </>
      )}
    </div>
  );
}
